import { useMonarchSlot } from "@redventures/cohesion-utils-react";
import Cookies from "js-cookie";
import { getBuyFlowFromParam } from "~/helpers/cartExperience";
import { setCookie } from "~/helpers/cookie";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BUY_FLOW, BUY_FLOW_COOKIE_KEY, BuyFlow } from "~/constants/buyFlow";
import { PAGE_ROUTES } from "~/constants/pages";
import { setNGCConfiguration } from "~/redux/actions/recommendation";
import { NGCConfiguration } from "~/types/recommendation";
import { ReduxState } from "~/types/redux";
import { cartConfigAddressCheckDefaultValue } from "../../monarch/Config | Address Check/default";
import { ngcFlowDefaultValue, NgcFlowSlotData } from "../../monarch/Config | NGC Flow/default";

export { BUY_FLOW };
export type { BuyFlow };

export const useBuyFlowDecision = ({ addressCheckCompleted }: { addressCheckCompleted: boolean }): void => {
  const router = useRouter();
  const dispatch = useDispatch();
  const { address, products, canNavigateToBuyFlowRedirect, enrichedCustomer } = useSelector(
    (store: ReduxState) => store
  );
  const { techAvailable } = address || {};
  const isCopper = techAvailable === "COPPER";
  const isCustomerEnrichmentCallFinished = enrichedCustomer !== undefined;
  const isCustomerEnrichmentRequestSuccessful =
    isCustomerEnrichmentCallFinished && Object.keys(enrichedCustomer).length > 0;
  const [buyFlow, setBuyFlow] = useState<BuyFlow | undefined>();
  const [persistedNGCConfiguration, setPersistedNGCConfiguration] = useState(false);

  /*
   * Read cookie value to make this hook reactive to cookie changes.
   * useRouteNGCTracker may write this cookie before Monarch resolves; the dep
   * here ensures we pick up that write and re-evaluate the priority ladder.
   */
  const buyFlowCookie = Cookies.get(BUY_FLOW_COOKIE_KEY) as BuyFlow | undefined;

  /* Address check slot - determines buy flow (NGC/BAU/DEFAULT) */
  const { isLoading: buyFlowLoading, data: buyFlowResult } = useMonarchSlot("addressCheck", {
    default: cartConfigAddressCheckDefaultValue,
  });

  /*
   * NGC Flow slot - provides NGC config values via traffic flow.
   * This replaces the previous separate Monarch rule approach.
   */
  const { data: ngcFlowData } = useMonarchSlot<NgcFlowSlotData>("cartConfigNgcFlows", {
    default: ngcFlowDefaultValue,
  });

  /*
   * useMonarchSlot returns isLoading=false on initial render before Monarch has
   * evaluated. Guard against defaulting to BAU on that first pass: only allow the
   * BAU fallback once Monarch has completed a full load cycle (isLoading true→false).
   * In the ~10% of sessions where pageViewed never fires and Monarch never evaluates,
   * useRouteNGCTracker will have written buyFlow=NGC via the cookie dep above if the
   * user reaches an NGC route, so the priority ladder still resolves correctly.
   */
  const hasMonarchLoaded = useRef(false);

  /*
   * Deadline ref for the stuck case: holds the setTimeout id while the 5-second
   * fallback timer is live. Cleared on cancel or fire.
   */
  const monarchDeadlineRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const canRedirect =
    address?.install?.address &&
    products?.available &&
    canNavigateToBuyFlowRedirect &&
    enrichedCustomer &&
    !buyFlowLoading;

  /* Effect that sets the buy flow (NGC or BAU) */
  useEffect(() => {
    if (!addressCheckCompleted) return;

    /*
     * Track when Monarch transitions to loading; the BAU fallback below is only
     * safe to use after Monarch has had a chance to return a real result.
     */
    if (buyFlowLoading) {
      hasMonarchLoaded.current = true;
      return;
    }

    const buyFlowQueryParam = getBuyFlowFromParam(router.query?.buyFlow);
    const cookieHasValidBuyFlow = !!buyFlowCookie && (buyFlowCookie === BUY_FLOW.NGC || buyFlowCookie === BUY_FLOW.BAU);
    const monarchBuyFlow = buyFlowResult?.buyFlow;
    const monarchBuyFlowIsValid =
      monarchBuyFlow === BUY_FLOW.NGC || monarchBuyFlow === BUY_FLOW.BAU || monarchBuyFlow === BUY_FLOW.DEFAULT;

    /*
     * Priority order:
     * 1. Query parameter (highest priority)
     * 2. Cookie value (set by NgcVsBau, useRouteNGCTracker, or other components)
     * 3. Monarch buyFlowResult
     * 4. BAU default — only after Monarch has completed a full evaluation cycle
     */
    if (buyFlowQueryParam) {
      setBuyFlow(buyFlowQueryParam);
    } else if (cookieHasValidBuyFlow) {
      setBuyFlow(buyFlowCookie);
    } else if (monarchBuyFlow && monarchBuyFlowIsValid) {
      setBuyFlow(monarchBuyFlow);
    } else if (hasMonarchLoaded.current) {
      /* Monarch has completed a load cycle but returned no usable buyFlow value. */
      setBuyFlow(BUY_FLOW.BAU);
    }
  }, [router.query?.buyFlow, buyFlowCookie, buyFlowResult, addressCheckCompleted, buyFlowLoading]);

  /*
   * Fallback for the ~10% of sessions where Cohesion never fires pageViewed and
   * Monarch never evaluates (isLoading stays false, hasMonarchLoaded.current never
   * becomes true). Without this, a genuine BAU user is indefinitely stuck on /buy.
   *
   * We arm a 5-second deadline matching useAddressCheck's existing loader timeout.
   * It is cancelled automatically when:
   *   (a) buyFlow is set by any other source (cookie, query param, Monarch result, or
   *       the hasMonarchLoaded guard after a real load cycle) — the dependency on
   *       `buyFlow` re-runs this effect, cleanup fires, and the guard `if (buyFlow)
   *       return` prevents re-arming; or
   *   (b) the component unmounts.
   *
   * The callback re-checks hasMonarchLoaded.current so that if Monarch started after
   * the arm but before the fire it cannot set a spurious BAU over the real result.
   */
  useEffect(() => {
    if (!addressCheckCompleted) return;
    if (hasMonarchLoaded.current) return; /* Monarch already completed; main effect handles it */
    if (buyFlow) return; /* Another source already resolved the flow; nothing to do */

    monarchDeadlineRef.current = setTimeout(() => {
      monarchDeadlineRef.current = null;
      if (!hasMonarchLoaded.current) {
        setBuyFlow(BUY_FLOW.BAU);
      }
    }, 5000);

    return () => {
      if (monarchDeadlineRef.current !== null) {
        clearTimeout(monarchDeadlineRef.current);
        monarchDeadlineRef.current = null;
      }
    };
  }, [addressCheckCompleted, buyFlow]);

  useEffect(() => {
    if (!addressCheckCompleted || !buyFlow) return;

    const isNGCWithEnrichmentFinished = buyFlow === BUY_FLOW.NGC && !isCopper && isCustomerEnrichmentCallFinished;

    if (isNGCWithEnrichmentFinished) {
      if (!isCustomerEnrichmentRequestSuccessful) {
        console.warn("useBuyFlowDecision: customer enrichment failed, falling back to BAU experience");
      }
      const enrichmentResolvedFlow = isCustomerEnrichmentRequestSuccessful ? BUY_FLOW.NGC : BUY_FLOW.BAU;
      setCookie(BUY_FLOW_COOKIE_KEY, enrichmentResolvedFlow, { otGroup: "C0003" });
    }

    if (buyFlow === BUY_FLOW.BAU) {
      setCookie(BUY_FLOW_COOKIE_KEY, BUY_FLOW.BAU, { otGroup: "C0003" });
    }
  }, [
    addressCheckCompleted,
    buyFlow,
    isCopper,
    isCustomerEnrichmentCallFinished,
    isCustomerEnrichmentRequestSuccessful,
  ]);

  /* Sets the NGC configuration from traffic flow slot. Only runs if buy flow is NGC */
  useEffect(() => {
    if (!addressCheckCompleted || buyFlow !== BUY_FLOW.NGC) return;

    /*
     * Get NGC configuration from the traffic flow slot data.
     * Map ngcFlow -> recommendationFlow for Redux state.
     */
    const slotData = ngcFlowData || ngcFlowDefaultValue;
    const ngcConfiguration: NGCConfiguration = {
      recommendationFlow: slotData.ngcFlow,
      model: slotData.model,
      showFaq: slotData.showFaq,
      showMoreDetailsByop: slotData.showMoreDetailsByop,
      altProductExplainerVersion: slotData.altProductExplainerVersion,
      v2AddOnCheckbox: slotData.v2AddOnCheckbox,
      claimRewardsLocation: slotData.claimRewardsLocation,
      enableStickyCtaOnQuiz: slotData.enableStickyCtaOnQuiz,
      showTraditionalCTA: slotData.showTraditionalCTA,
      simplifiedRecommendedPlansCta: slotData.simplifiedRecommendedPlansCta ?? undefined,
      defaultExpandedPlanCards:
        "defaultExpandedPlanCards" in slotData
          ? (slotData.defaultExpandedPlanCards as NGCConfiguration["defaultExpandedPlanCards"])
          : undefined,
      enableAbove3RecLtoBanner: slotData.enableAbove3RecLtoBanner ?? false,
      broadbandBelowCards: slotData.broadbandBelowCards ?? false,
    };
    dispatch(setNGCConfiguration(ngcConfiguration));
    setPersistedNGCConfiguration(true);
  }, [ngcFlowData, dispatch, buyFlow, addressCheckCompleted]);

  useEffect(() => {
    if (!canRedirect || !buyFlow) return;
    if (buyFlow === BUY_FLOW.NGC && !persistedNGCConfiguration) return;

    const isNGCWithEnrichmentFinished = buyFlow === BUY_FLOW.NGC && !isCopper && isCustomerEnrichmentCallFinished;

    if (buyFlow === BUY_FLOW.DEFAULT) {
      router.push(PAGE_ROUTES.BUY_FLOW_REDIRECT);
    } else if (isNGCWithEnrichmentFinished) {
      if (!isCustomerEnrichmentRequestSuccessful) {
        setCookie(BUY_FLOW_COOKIE_KEY, BUY_FLOW.BAU, { otGroup: "C0003" });
        router.push(PAGE_ROUTES.BUY_FLOW_REDIRECT);
      } else {
        setCookie(BUY_FLOW_COOKIE_KEY, BUY_FLOW.NGC, { otGroup: "C0003" });
        router.push(PAGE_ROUTES.QUIZ);
        window.sessionStorage.setItem("hasEnteredNGCQuiz", "true");
      }
    } else {
      setCookie(BUY_FLOW_COOKIE_KEY, BUY_FLOW.BAU, { otGroup: "C0003" });
      router.push(PAGE_ROUTES.BUY_FLOW_REDIRECT);
    }
  }, [
    canRedirect,
    buyFlow,
    isCopper,
    router,
    persistedNGCConfiguration,
    isCustomerEnrichmentCallFinished,
    isCustomerEnrichmentRequestSuccessful,
  ]);
};
